import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { CheckCircle2, Clock, RotateCcw, Timer, Trophy, XCircle } from "lucide-react";
import { DesempenhoPeriodFilter, descreverPeriodo } from "./DesempenhoPeriodFilter";

/**
 * MobileDesempenhoDialog — Painel "Meu Desempenho" do entregador (fev/2026, v5).
 * Consome GET /api/minhas-entregas/desempenho?periodo=hoje|semana|mes|personalizado(&inicio&fim).
 * Mostra TODAS as entregas atribuídas no período escolhido, quebradas por dia
 * e por status + tempo médio de conclusão. Filtro de período no topo (jun/2026).
 */
const fmtDataPtBR = (isoDate) => {
  try {
    const [y, m, d] = isoDate.split("-").map(Number);
    const dt = new Date(y, m - 1, d);
    return dt.toLocaleDateString("pt-BR", { day: "2-digit", month: "short", weekday: "short" }).replace(",", " ·");
  } catch {
    return isoDate;
  }
};

const fmtTempo = (min) => (min == null ? "—" : `${Number(min).toFixed(1)} min`);

const STATUS_META = [
  { key: "concluidas", label: "Entregues", Icon: CheckCircle2, tone: "text-emerald-700 bg-emerald-50 border-emerald-200" },
  { key: "pendentes", label: "Pendentes", Icon: Clock, tone: "text-blue-700 bg-blue-50 border-blue-200" },
  { key: "remarcadas", label: "Remarcadas", Icon: RotateCcw, tone: "text-amber-700 bg-amber-50 border-amber-200" },
  { key: "canceladas", label: "Canceladas", Icon: XCircle, tone: "text-rose-700 bg-rose-50 border-rose-200" },
];

const TOTAL_TILES = [
  { key: "concluidas", label: "Entregues", cls: "bg-emerald-500" },
  { key: "pendentes", label: "Pendentes", cls: "bg-blue-500" },
  { key: "remarcadas", label: "Remarcadas", cls: "bg-amber-500" },
  { key: "canceladas", label: "Canceladas", cls: "bg-rose-500" },
];

const DayRow = ({ d }) => (
  <li className="px-3 py-2 bg-white border-b last:border-b-0" data-testid={`desempenho-dia-${d.data}`}>
    <div className="flex items-center justify-between mb-1">
      <span className="text-sm font-semibold capitalize">{fmtDataPtBR(d.data)}</span>
      <span className="text-sm font-extrabold text-orange-700 mono">{d.total} total</span>
    </div>
    <div className="flex flex-wrap gap-1">
      {STATUS_META.filter(s => d[s.key] > 0).map(({ key, label, Icon, tone }) => (
        <span key={key} className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-semibold ${tone}`} data-testid={`dia-${d.data}-${key}`}>
          <Icon className="w-3 h-3" />{d[key]} {label.toLowerCase()}
        </span>
      ))}
      {d.tempo_medio_min != null && (
        <span className="inline-flex items-center gap-1 rounded-full border border-orange-200 bg-orange-50 text-orange-700 px-2 py-0.5 text-[11px] font-semibold" data-testid={`dia-${d.data}-tempo`}>
          <Timer className="w-3 h-3" />médio {fmtTempo(d.tempo_medio_min)}
        </span>
      )}
    </div>
  </li>
);

export function MobileDesempenhoDialog({ open, onOpenChange }) {
  const hojeIso = new Date().toLocaleDateString("sv-SE");
  const [filtro, setFiltro] = useState({ periodo: "mes", inicio: hojeIso, fim: hojeIso });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const custom = filtro.periodo === "personalizado";
  const prontoParaBuscar = !custom || (filtro.inicio && filtro.fim && filtro.inicio <= filtro.fim);

  useEffect(() => {
    if (!open || !prontoParaBuscar) return;
    let cancelled = false;
    setLoading(true); setError(null);
    const params = custom ? { periodo: "personalizado", inicio: filtro.inicio, fim: filtro.fim } : { periodo: filtro.periodo };
    api.get("/minhas-entregas/desempenho", { params })
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch((e) => { if (!cancelled) setError(e?.response?.data?.detail || "Não foi possível carregar seu desempenho"); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [open, filtro.periodo, filtro.inicio, filtro.fim, custom, prontoParaBuscar]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md max-h-[92vh] overflow-y-auto" data-testid="dialog-desempenho">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-orange-700">
            <Trophy className="w-5 h-5" />Meu Desempenho
          </DialogTitle>
        </DialogHeader>

        <div className="pb-1">
          <DesempenhoPeriodFilter value={filtro} onChange={setFiltro} />
        </div>

        {loading && (
          <div className="py-10 text-center text-muted-foreground text-sm" data-testid="desempenho-loading">Carregando…</div>
        )}
        {error && !loading && (
          <div className="py-6 text-center text-rose-600 text-sm" data-testid="desempenho-error">{error}</div>
        )}
        {data && !loading && !error && (
          <div className="space-y-4">
            <div className="bg-gradient-to-r from-orange-500 to-orange-600 text-white rounded-xl p-4 shadow-md" data-testid="desempenho-mes-total">
              <div className="flex items-start justify-between">
                <div>
                  <div className="text-[11px] uppercase font-bold tracking-widest opacity-90">Total no período</div>
                  <div className="text-4xl font-extrabold mt-1 mono">{data.mes_total.total}</div>
                  <div className="text-xs opacity-90 mt-1" data-testid="desempenho-periodo-label">entregas atribuídas {descreverPeriodo(data.periodo, data.inicio, data.fim)}</div>
                </div>
                <div className="text-right" data-testid="desempenho-tempo-medio-mes">
                  <div className="text-[10px] uppercase font-bold tracking-wider opacity-85 flex items-center gap-1 justify-end">
                    <Timer className="w-3 h-3" />Tempo médio
                  </div>
                  <div className="text-2xl font-extrabold mono mt-0.5">{fmtTempo(data.mes_total.tempo_medio_min)}</div>
                  <div className="text-[10px] opacity-85">por entrega</div>
                </div>
              </div>
              <div className="grid grid-cols-4 gap-1.5 mt-3">
                {TOTAL_TILES.map(({ key, label, cls }) => (
                  <div key={key} className={`${cls} rounded-md py-1.5 text-center shadow-sm`} data-testid={`mes-${key}`}>
                    <div className="text-lg font-extrabold mono leading-none">{data.mes_total[key]}</div>
                    <div className="text-[9px] uppercase font-bold tracking-wide opacity-95 mt-0.5">{label}</div>
                  </div>
                ))}
              </div>
            </div>

            <section data-testid="desempenho-por-dia">
              <h3 className="text-xs font-bold uppercase text-orange-800 tracking-wider mb-2">Por dia</h3>
              {data.por_dia.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground text-sm border rounded-lg border-dashed">
                  Nenhuma entrega atribuída no período.
                </div>
              ) : (
                <ul className="border rounded-lg overflow-hidden">
                  {data.por_dia.map((d) => (<DayRow key={d.data} d={d} />))}
                </ul>
              )}
            </section>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
